import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Zap, CheckCircle, TrendingDown, Shield, Clock, X, RefreshCw } from 'lucide-react';
import { optimizationApi } from '../services/api';
import {
  Card, SectionHeader, Loader, StatCard, Badge, Button, EmptyState,
} from '../components/ui';
import toast from 'react-hot-toast';
import { useRole } from '../hooks/useRole';

const DIMENSIONS = [
  'ALL', 'COMPUTE', 'STORAGE', 'NETWORK', 'DATABASE', 'APPLICATION', 'OPERATIONS', 'LICENSING', 'ENERGY', 'FACILITY',
];

const RISK_COLORS = {
  Low: { bg: 'rgba(0,200,150,0.15)', text: '#00c896', border: 'rgba(0,200,150,0.3)' },
  Medium: { bg: 'rgba(255,184,0,0.15)', text: '#ffb800', border: 'rgba(255,184,0,0.3)' },
  High: { bg: 'rgba(255,92,92,0.15)', text: '#ff5c5c', border: 'rgba(255,92,92,0.3)' },
};

// Mock rollback plans and extra details
const MOCK_DETAILS = {
  rollbackPlan: 'Revert to previous instance type/configuration. Snapshot will be preserved for 7 days. Estimated rollback time: 5 minutes.',
  estimatedDowntime: '< 2 minutes (rolling update)',
};

function fmt(n) {
  if (n === null || n === undefined || n === 0) return 'No data';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${Number(n).toFixed(2)}`;
}

function ConfirmDialog({ title, message, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999,
    }}>
      <div style={{
        background: '#0d1527', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 14, padding: 28, maxWidth: 420, width: '90%',
        boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>{title}</div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
          {message}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
          <Button variant="success" size="sm" onClick={onConfirm}>Confirm Apply</Button>
        </div>
      </div>
    </div>
  );
}

const DIM_COLORS = {
  COMPUTE:    { color: '#ff5c5c', bg: 'rgba(255,92,92,0.12)'   },
  STORAGE:    { color: '#ff9900', bg: 'rgba(255,153,0,0.12)'   },
  NETWORK:    { color: '#0084ff', bg: 'rgba(0,132,255,0.12)'   },
  DATABASE:   { color: '#a855f7', bg: 'rgba(168,85,247,0.12)'  },
  OPERATIONS: { color: '#00c896', bg: 'rgba(0,200,150,0.12)'   },
  LICENSING:  { color: '#ffb800', bg: 'rgba(255,184,0,0.12)'   },
};

function LiveAwsOptimizations() {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['live-analysis'],
    queryFn: () => optimizationApi.getLiveAnalysis().then((r) => r.data),
    refetchInterval: 60000,
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ['live-analysis'] });
    await queryClient.invalidateQueries({ queryKey: ['opt-summary'] });
    await queryClient.invalidateQueries({ queryKey: ['opt-recs'] });
    setTimeout(() => setRefreshing(false), 2000);
  };

  if (isLoading) return (
    <div style={{ marginTop: 36 }}>
      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, marginBottom: 12 }}>
        Running rules engine against live AWS billing data…
      </div>
      <Loader />
    </div>
  );

  if (isError) return (
    <div style={{ marginTop: 36 }}>
      <EmptyState message="Could not run live analysis. Ensure the backend is running with valid AWS credentials." />
    </div>
  );

  const recs = data?.recommendations || [];
  const totalSavings = data?.totalPotentialSavings || 0;
  const triggered = data?.triggeredRules || 0;
  const total = data?.totalRules || 0;

  return (
    <div style={{ marginTop: 36 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 17, marginBottom: 4 }}>
            Live AWS Cost Analysis
          </div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
            Rules engine analysed your billing data — {triggered}/{total} rules triggered · {data?.analysedPeriod}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={handleRefresh} disabled={refreshing} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            background: 'rgba(0,132,255,0.12)', color: '#4a9eff',
            border: '1px solid rgba(0,132,255,0.25)', cursor: refreshing ? 'default' : 'pointer',
            opacity: refreshing ? 0.6 : 1, transition: 'all 0.2s',
          }}>
            <RefreshCw size={14} style={{
              animation: refreshing ? 'spin 1s linear infinite' : 'none',
            }} />
            {refreshing ? 'Refreshing...' : 'Refresh Analysis'}
          </button>
          <div style={{
            background: 'rgba(0,200,150,0.1)', border: '1px solid rgba(0,200,150,0.25)',
            borderRadius: 10, padding: '10px 18px', textAlign: 'right',
          }}>
            <div style={{ color: '#00c896', fontWeight: 800, fontSize: 22 }}>{fmt(totalSavings * 12)}</div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>potential/year</div>
          </div>
        </div>
      </div>

      {/* Rule summary pills */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {Object.entries(
          recs.reduce((acc, r) => { acc[r.dimension] = (acc[r.dimension] || 0) + 1; return acc; }, {})
        ).map(([dim, count]) => {
          const c = DIM_COLORS[dim] || { color: '#888', bg: 'rgba(136,136,136,0.1)' };
          return (
            <span key={dim} style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
              background: c.bg, color: c.color, border: `1px solid ${c.color}33`,
            }}>
              {dim} · {count}
            </span>
          );
        })}
      </div>

      {recs.length === 0 ? (
        <EmptyState message="No optimization opportunities detected from current billing data." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {recs.map((rec) => {
            const dimStyle = DIM_COLORS[rec.dimension] || { color: '#888', bg: 'rgba(136,136,136,0.1)' };
            const riskColors = RISK_COLORS[rec.riskLevel] || RISK_COLORS.Medium;
            const confidence = rec.confidence || 0;
            const saving = parseFloat(rec.estimatedMonthlySavings || 0);
            const current = parseFloat(rec.currentMonthlyCost || 0);

            return (
              <Card key={rec.id} style={{ padding: 22, borderLeft: `3px solid ${dimStyle.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20 }}>
                  {/* Left */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Badges */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                        background: dimStyle.bg, color: dimStyle.color, border: `1px solid ${dimStyle.color}44`,
                      }}>
                        {rec.dimension}
                      </span>
                      <Badge label={rec.priority} type={rec.priority} />
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                        background: 'rgba(255,153,0,0.1)', color: '#ff9900',
                        border: '1px solid rgba(255,153,0,0.25)',
                      }}>
                        LIVE
                      </span>
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                        background: riskColors.bg, color: riskColors.text,
                        border: `1px solid ${riskColors.border}`,
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}>
                        <Shield size={10} /> {rec.riskLevel} Risk
                      </span>
                      {rec.tags && rec.tags.slice(2).map((tag) => (
                        <span key={tag} style={{
                          padding: '3px 8px', borderRadius: 20, fontSize: 10, fontWeight: 500,
                          background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.3)',
                          border: '1px solid rgba(255,255,255,0.08)',
                        }}>
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Title */}
                    <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, marginBottom: 6, lineHeight: 1.4 }}>
                      {rec.title}
                    </div>

                    {/* Description */}
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, lineHeight: 1.6, marginBottom: 10 }}>
                      {rec.description}
                    </div>

                    {/* Technology upgrade path */}
                    {rec.technology && (
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '4px 10px', borderRadius: 6, marginBottom: 10,
                        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                        fontSize: 12, color: 'rgba(255,255,255,0.45)', fontFamily: 'monospace',
                      }}>
                        {rec.technology}
                      </div>
                    )}

                    {/* Action steps */}
                    {rec.actionSteps && (
                      <details style={{ cursor: 'pointer', marginBottom: 6 }}>
                        <summary style={{ color: '#4a9eff', fontSize: 12, fontWeight: 500, outline: 'none' }}>
                          View action steps
                        </summary>
                        <pre style={{
                          color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 8,
                          whiteSpace: 'pre-wrap', lineHeight: 1.7, fontFamily: 'inherit',
                          padding: '10px 14px', background: 'rgba(255,255,255,0.02)',
                          borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)',
                        }}>
                          {rec.actionSteps}
                        </pre>
                      </details>
                    )}

                    {/* Rationale */}
                    {rec.rationale && (
                      <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, marginTop: 4 }}>
                        {rec.rationale}
                      </div>
                    )}
                  </div>

                  {/* Right: savings */}
                  <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 150 }}>
                    <div style={{ color: '#00c896', fontSize: 26, fontWeight: 800, letterSpacing: '-1px' }}>
                      {fmt(saving * 12)}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, marginBottom: 4 }}>
                      /year savings
                    </div>
                    {current > 0 && (
                      <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 11, marginBottom: 12 }}>
                        of {fmt(current * 12)}/yr current spend
                      </div>
                    )}
                    <div>
                      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginBottom: 4 }}>
                        Confidence: {(confidence * 100).toFixed(0)}%
                      </div>
                      <div style={{ height: 6, background: 'rgba(255,255,255,0.07)', borderRadius: 3, overflow: 'hidden', width: 120, marginLeft: 'auto' }}>
                        <div style={{
                          height: '100%', width: `${confidence * 100}%`,
                          background: confidence > 0.8 ? '#00c896' : confidence > 0.5 ? '#ffb800' : '#ff5c5c',
                          borderRadius: 3,
                        }} />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function OptimizationPage() {
  const [selectedDimension, setSelectedDimension] = useState('ALL');
  const [confirmApply, setConfirmApply] = useState(null);
  const qc = useQueryClient();
  const { isEndUser, isStaffOrAbove } = useRole();

  const { data: summary, isLoading: loadingSummary } = useQuery({
    queryKey: ['opt-summary'],
    queryFn: () => optimizationApi.getSummary().then((r) => r.data),
  });

  const { data: recs = [], isLoading: loadingRecs } = useQuery({
    queryKey: ['recommendations', selectedDimension],
    queryFn: () =>
      optimizationApi
        .getRecommendations(selectedDimension === 'ALL' ? null : selectedDimension)
        .then((r) => r.data),
  });

  const approveMut = useMutation({
    mutationFn: (id) => optimizationApi.approve(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recommendations'] });
      toast.success('Recommendation approved');
    },
    onError: () => toast.error('Failed to approve recommendation'),
  });

  const applyMut = useMutation({
    mutationFn: (id) => optimizationApi.apply(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recommendations'] });
      qc.invalidateQueries({ queryKey: ['opt-summary'] });
      toast.success('Recommendation applied! Savings will reflect in next billing cycle.');
      setConfirmApply(null);
    },
    onError: () => {
      toast.error('Failed to apply recommendation');
      setConfirmApply(null);
    },
  });

  const handleApplyClick = (rec) => {
    setConfirmApply(rec);
  };

  const handleConfirmApply = () => {
    if (confirmApply) {
      applyMut.mutate(confirmApply.id);
    }
  };

  return (
    <div>
      <SectionHeader
        title={isEndUser ? 'Your Savings' : 'Optimization Engine'}
        subtitle={isEndUser
          ? 'Summary of savings identified for your account. Contact your SmalBlu advisor for details.'
          : 'AI-powered recommendations across all 7 cost dimensions'}
      />

      {/* Confirmation Dialog */}
      {confirmApply && (
        <ConfirmDialog
          title="Apply Recommendation"
          message={`Are you sure you want to apply "${confirmApply.title}"? This will make changes to your infrastructure. Estimated downtime: ${MOCK_DETAILS.estimatedDowntime}.`}
          onConfirm={handleConfirmApply}
          onCancel={() => setConfirmApply(null)}
        />
      )}

      {/* Live AWS Optimizations - top position (drives all KPIs) */}
      {!isEndUser && <LiveAwsOptimizations />}

      {/* Dimension Filter Tabs */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 22,
        flexWrap: 'wrap',
      }}>
        {DIMENSIONS.map((dim) => (
          <button
            key={dim}
            onClick={() => setSelectedDimension(dim)}
            style={{
              padding: '6px 16px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              border: `1px solid ${selectedDimension === dim ? '#0084ff' : 'rgba(255,255,255,0.1)'}`,
              background: selectedDimension === dim ? 'rgba(0,132,255,0.15)' : 'transparent',
              color: selectedDimension === dim ? '#4a9eff' : 'rgba(255,255,255,0.45)',
              transition: 'all 0.15s ease',
              fontFamily: 'inherit',
            }}
          >
            {dim}
          </button>
        ))}
      </div>

      {/* Saved Recommendations - only show if tenant has data */}
      {recs.length > 0 && (
        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12, marginTop: 24 }}>
          Saved Recommendations ({recs.length})
        </div>
      )}
      {loadingRecs ? null : recs.length === 0 ? null : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {recs.map((rec) => {
            const riskLevel = rec.riskLevel || 'Medium';
            const riskColors = RISK_COLORS[riskLevel] || RISK_COLORS.Medium;
            const confidence = rec.confidenceScore != null ? rec.confidenceScore : 0.85;

            return (
              <Card key={rec.id} style={{ padding: 22 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 20,
                }}>
                  {/* Left: Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Badges */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 10,
                      flexWrap: 'wrap',
                    }}>
                      <Badge label={rec.priority} type={rec.priority} />
                      <Badge label={rec.dimension} type="PENDING" />
                      <Badge label={rec.status} type={rec.status} />
                      {/* Risk Level Badge */}
                      <span style={{
                        padding: '3px 10px',
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        background: riskColors.bg,
                        color: riskColors.text,
                        border: `1px solid ${riskColors.border}`,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                      }}>
                        <Shield size={10} />
                        {riskLevel} Risk
                      </span>
                    </div>

                    {/* Title */}
                    <div style={{
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 15,
                      marginBottom: 6,
                      lineHeight: 1.4,
                    }}>
                      {rec.title}
                    </div>

                    {/* Description */}
                    {!isEndUser && rec.description && (
                    <div style={{
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: 13,
                      lineHeight: 1.6,
                      marginBottom: 10,
                    }}>
                      {rec.description}
                    </div>
                    )}
                    {isEndUser && (
                    <div style={{
                      color: 'rgba(255,255,255,0.4)',
                      fontSize: 12,
                      fontStyle: 'italic',
                      marginBottom: 10,
                    }}>
                      Contact your SmalBlu advisor to unlock recommendation details.
                    </div>
                    )}

                    {/* Action Steps */}
                    {!isEndUser && rec.actionSteps && (
                      <details style={{ cursor: 'pointer', marginBottom: 10 }}>
                        <summary style={{
                          color: '#4a9eff',
                          fontSize: 12,
                          fontWeight: 500,
                          outline: 'none',
                        }}>
                          View action steps
                        </summary>
                        <pre style={{
                          color: 'rgba(255,255,255,0.5)',
                          fontSize: 12,
                          marginTop: 8,
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.7,
                          fontFamily: 'inherit',
                          padding: '10px 14px',
                          background: 'rgba(255,255,255,0.02)',
                          borderRadius: 8,
                          border: '1px solid rgba(255,255,255,0.05)',
                        }}>
                          {rec.actionSteps}
                        </pre>
                      </details>
                    )}

                    {/* Rollback Plan */}
                    {!isEndUser && (
                    <details style={{ cursor: 'pointer', marginBottom: 10 }}>
                      <summary style={{
                        color: '#ffb800',
                        fontSize: 12,
                        fontWeight: 500,
                        outline: 'none',
                      }}>
                        Rollback plan
                      </summary>
                      <div style={{
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: 12,
                        marginTop: 8,
                        lineHeight: 1.7,
                        padding: '10px 14px',
                        background: 'rgba(255,184,0,0.04)',
                        borderRadius: 8,
                        border: '1px solid rgba(255,184,0,0.1)',
                      }}>
                        {rec.rollbackPlan || MOCK_DETAILS.rollbackPlan}
                      </div>
                    </details>
                    )}

                    {/* Estimated Downtime */}
                    {!isEndUser && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      color: 'rgba(255,255,255,0.4)',
                      fontSize: 11,
                    }}>
                      <Clock size={11} />
                      Estimated downtime: {rec.estimatedDowntime || MOCK_DETAILS.estimatedDowntime}
                    </div>
                    )}
                  </div>

                  {/* Right: Savings & Actions */}
                  <div style={{
                    textAlign: 'right',
                    flexShrink: 0,
                    minWidth: 140,
                  }}>
                    <div style={{
                      color: '#00c896',
                      fontSize: 24,
                      fontWeight: 800,
                      letterSpacing: '-1px',
                    }}>
                      {fmt(rec.estimatedMonthlySavings * 12)}
                    </div>
                    <div style={{
                      color: 'rgba(255,255,255,0.35)',
                      fontSize: 11,
                      marginBottom: 12,
                    }}>
                      /year savings
                    </div>

                    {/* Confidence Score Bar */}
                    {!isEndUser && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginBottom: 4 }}>
                        Confidence: {(confidence * 100).toFixed(0)}%
                      </div>
                      <div style={{
                        height: 6, background: 'rgba(255,255,255,0.07)', borderRadius: 3,
                        overflow: 'hidden', width: 120, marginLeft: 'auto',
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${confidence * 100}%`,
                          background: confidence > 0.8 ? '#00c896' : confidence > 0.5 ? '#ffb800' : '#ff5c5c',
                          borderRadius: 3,
                          transition: 'width 0.4s ease',
                        }} />
                      </div>
                    </div>
                    )}

                    {isStaffOrAbove && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                    }}>
                      {rec.status === 'PENDING' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => approveMut.mutate(rec.id)}
                          disabled={approveMut.isPending}
                        >
                          Approve
                        </Button>
                      )}
                      {rec.status === 'APPROVED' && (
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => handleApplyClick(rec)}
                          disabled={applyMut.isPending}
                        >
                          Apply Now
                        </Button>
                      )}
                    </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

    </div>
  );
}
